import * as React from 'react';

import {
  Button,
  ConsoleDialog,
  NumberField,
  Select,
  Switch,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import type { InventoryPeriodConfigRecord } from '../../_graphql/queryInventoryPeriodConfig';
import {
  DEFAULT_FORM,
  type PeriodConfigForm,
} from '../../_state/inventoryPeriodConfig.state';
import * as configState from '../../_state/inventoryPeriodConfig.state';
import { useTranslations } from '../../i18n/useTranslations';

type InventoryPeriodConfigDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  existingConfig?: InventoryPeriodConfigRecord | null;
};

export const InventoryPeriodConfigDialog = ({
  isOpen,
  onClose,
  existingConfig,
}: InventoryPeriodConfigDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [formData, setFormData] = useAtom(configState.$periodConfigForm);
  const { isFiscalPeriod, showStraddleFields, canSubmit } = useAtomValue(
    configState.$periodConfigFormConstraints,
  );
  const resetForm = useSetAtom(configState.$resetPeriodConfigForm);

  const [{ isPending: isCreating }, createConfig] = useAtom(
    configState.$createPeriodConfig,
  );
  const [{ isPending: isUpdating }, updateConfig] = useAtom(
    configState.$updatePeriodConfig,
  );

  const isEditMode = !!existingConfig;
  const isSubmitting = isCreating || isUpdating;

  const handleFieldChange =
    <F extends keyof PeriodConfigForm>(field: F) =>
    (value: PeriodConfigForm[F]) => {
      setFormData((form) => ({ ...form, [field]: value }));
    };

  React.useEffect(() => {
    if (!isOpen) return;

    if (existingConfig) {
      setFormData({
        periodType: existingConfig.periodType,
        fiscalYearStartMonth: existingConfig.fiscalYearStartMonth,
        allowStraddleCompletion: existingConfig.allowStraddleCompletion,
        straddleWindowPeriods: existingConfig.straddleWindowPeriods,
        requireStraddleApproval: existingConfig.requireStraddleApproval,
      });
    } else {
      resetForm();
    }
  }, [isOpen, existingConfig, setFormData, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (isFiscalPeriod && !formData.fiscalYearStartMonth) {
      toast.error(
        t.periodsListView.periodConfig.validation.fiscalYearStartMonthRequired,
      );
      return;
    }

    try {
      const payload = {
        periodType: formData.periodType ?? DEFAULT_FORM.periodType,
        fiscalYearStartMonth: isFiscalPeriod
          ? (formData.fiscalYearStartMonth ?? null)
          : null,
        allowStraddleCompletion:
          formData.allowStraddleCompletion ??
          DEFAULT_FORM.allowStraddleCompletion,
        straddleWindowPeriods:
          formData.straddleWindowPeriods ?? DEFAULT_FORM.straddleWindowPeriods,
        requireStraddleApproval:
          formData.requireStraddleApproval ??
          DEFAULT_FORM.requireStraddleApproval,
      };

      if (isEditMode && existingConfig) {
        await updateConfig({ id: existingConfig.id, ...payload });
        toast.success(t.periodsListView.periodConfig.messages.updateSuccess);
      } else {
        await createConfig(payload);
        toast.success(t.periodsListView.periodConfig.messages.createSuccess);
      }

      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error(
        isEditMode
          ? t.periodsListView.periodConfig.messages.updateError(errorMessage)
          : t.periodsListView.periodConfig.messages.createError(errorMessage),
      );
    }
  }, [
    formData,
    isFiscalPeriod,
    isEditMode,
    existingConfig,
    createConfig,
    updateConfig,
    toast,
    t,
    handleClose,
  ]);

  const periodTypeOptions = React.useMemo(
    () =>
      Object.entries(t.periodsListView.periodConfig.periodTypeOptions).map(
        ([key, label]) => ({ key, label }),
      ),
    [t],
  );

  const monthOptions = React.useMemo(
    () =>
      Object.entries(t.periodsListView.periodConfig.months).map(
        ([key, label]) => ({ key, label }),
      ),
    [t],
  );

  const dialogTitle = isEditMode
    ? t.periodsListView.periodConfig.editTitle
    : t.periodsListView.periodConfig.createTitle;

  return (
    <ConsoleDialog
      className="@InventoryPeriodConfigDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="text-sm font-semibold">
              {t.periodsListView.periodConfig.sections.periodSettings}
            </div>

            <Select
              label={t.periodsListView.periodConfig.fields.periodType}
              value={formData.periodType}
              onChange={(key) => handleFieldChange('periodType')(String(key))}
              options={periodTypeOptions}
              isRequired
            />

            {isFiscalPeriod && (
              <Select
                label={
                  t.periodsListView.periodConfig.fields.fiscalYearStartMonth
                }
                value={
                  formData.fiscalYearStartMonth
                    ? String(formData.fiscalYearStartMonth)
                    : ''
                }
                onChange={(key) =>
                  handleFieldChange('fiscalYearStartMonth')(Number(key))
                }
                options={monthOptions}
                isRequired
              />
            )}
          </div>

          <div className="space-y-4">
            <div className="text-sm font-semibold">
              {t.periodsListView.periodConfig.sections.straddleSettings}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {t.periodsListView.periodConfig.fields.allowStraddleCompletion}
              </span>
              <Switch
                isSelected={formData.allowStraddleCompletion}
                onChange={handleFieldChange('allowStraddleCompletion')}
              />
            </div>

            {showStraddleFields && (
              <>
                <NumberField
                  label={
                    t.periodsListView.periodConfig.fields.straddleWindowPeriods
                  }
                  value={formData.straddleWindowPeriods}
                  onChange={handleFieldChange('straddleWindowPeriods')}
                  minValue={1}
                  maxValue={12}
                />

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {
                      t.periodsListView.periodConfig.fields
                        .requireStraddleApproval
                    }
                  </span>
                  <Switch
                    isSelected={formData.requireStraddleApproval}
                    onChange={handleFieldChange('requireStraddleApproval')}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.periodsListView.periodConfig.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {t.periodsListView.periodConfig.actions.save}
          </Button>
        </div>
      }
    />
  );
};
